import { isDecimalAmount } from '@platform/design-system';
import { detectDelimiter, parseCsv } from './csv.ts';

export type RemittanceEntry = {
  readonly trackingNumber: string;
  readonly amount: string | null;
  readonly fee: string | null;
};

export type ParsedRemittance = {
  readonly lines: readonly RemittanceEntry[];
  readonly invalidRows: readonly number[];
};

type ColumnRole = 'tracking' | 'fee' | 'amount';
type Columns = Readonly<Record<ColumnRole, number | null>>;

const HEADER_PATTERNS: readonly (readonly [ColumnRole, RegExp])[] = [
  ['tracking', /track|barcode|bar code|code.?barre|suivi|awb|colis/iu],
  ['fee', /fee|frais|cost|charge|co[uû]t/iu],
  ['amount', /cod|amount|montant|prix|price|valeur|value/iu],
];

const POSITIONAL: Columns = { tracking: 0, amount: 1, fee: 2 };
const PASTE_SEPARATOR = /[\t; ]+/u;
const SPACES = /[\s  ]/gu;
const MAX_CODE_LENGTH = 80;

const roleOf = (header: string): ColumnRole | null =>
  HEADER_PATTERNS.find(([, pattern]) => pattern.test(header))?.[0] ?? null;

const headerColumns = (row: readonly string[]): Columns | null => {
  const roles = row.map(roleOf);
  const indexOf = (role: ColumnRole): number | null => {
    const index = roles.indexOf(role);
    return index < 0 ? null : index;
  };
  const tracking = indexOf('tracking');
  return tracking === null ? null : { tracking, amount: indexOf('amount'), fee: indexOf('fee') };
};

const INVALID = Symbol('invalid');

const amountOf = (raw: string | undefined): string | null | typeof INVALID => {
  const compact = (raw ?? '').replace(SPACES, '');
  if (compact === '') return null;
  const decimal = compact.includes('.') ? compact : compact.replace(',', '.');
  return isDecimalAmount(decimal) ? decimal : INVALID;
};

const cellAt = (row: readonly string[], index: number | null): string | undefined =>
  index === null ? undefined : row[index];

const entriesOf = (
  rows: readonly (readonly string[])[],
  columns: Columns,
  firstRowNumber: number,
): ParsedRemittance => {
  const lines: RemittanceEntry[] = [];
  const invalidRows: number[] = [];
  rows.forEach((row, index) => {
    const trackingNumber = (cellAt(row, columns.tracking) ?? '').trim();
    const amount = amountOf(cellAt(row, columns.amount));
    const fee = amountOf(cellAt(row, columns.fee));
    if (trackingNumber === '') return;
    if (amount === INVALID || fee === INVALID || trackingNumber.length > MAX_CODE_LENGTH)
      invalidRows.push(firstRowNumber + index);
    else lines.push({ trackingNumber, amount, fee });
  });
  return { lines, invalidRows };
};

export const parseRemittanceCsv = (text: string): ParsedRemittance => {
  const rows = parseCsv(text, detectDelimiter(text));
  const [first, ...rest] = rows;
  const columns = first === undefined ? null : headerColumns(first);
  return columns === null ? entriesOf(rows, POSITIONAL, 1) : entriesOf(rest, columns, 2);
};

export const parsePastedCodes = (text: string): ParsedRemittance =>
  entriesOf(
    text.split(/\r?\n/u).map((line) => line.trim().split(PASTE_SEPARATOR)),
    POSITIONAL,
    1,
  );
